/* eslint-disable react/display-name */
import React, { Fragment, useState, forwardRef, useRef } from 'react';
import { NavLink as RouterLink } from 'react-router-dom';
import clsx from 'clsx';
import PropTypes from 'prop-types';
import { makeStyles } from '@material-ui/styles';
import { ListItem, Button, Collapse, colors, IconButton, Typography, Tooltip } from '@material-ui/core';
import InsertDriveFileIcon from '@material-ui/icons/InsertDriveFile';
import FolderIcon from '@material-ui/icons/Folder';
import FolderOpenIcon from '@material-ui/icons/FolderOpen';
import AddIcon from '@material-ui/icons/Add';
import FileCopyIcon from '@material-ui/icons/FileCopy';
import DeleteForeverIcon from '@material-ui/icons/DeleteForever';
import { DeleteModal } from 'components';
import Popover from '../../../Popover'
import ShareIcon from '@material-ui/icons/Share';

const CustomRouterLink = forwardRef((props, ref) => {
  return (
    <div
      ref={ref}
      style={{ flexGrow: 1 }}
    >
      <RouterLink {...props} />
    </div>
  );
});

const useStyles = makeStyles(theme => ({
  item: {
    display: 'block',
    paddingTop: 0,
    paddingBottom: 0,
    '&:hover $mla': {
      opacity: 1
    }
  },
  itemLeaf: {
    display: 'flex',
    paddingTop: 0,
    paddingBottom: 0,
    '&:hover $mla': {
      opacity: 1
    }
  },
  button: {
    color: colors.blueGrey[800],
    padding: '10px 8px',
    justifyContent: 'flex-start',
    textTransform: 'none',
    letterSpacing: 0,
    width: '100%'
  },
  buttonLabel: {width: '100%',
    display: 'inherit',
    alignItems: 'inherit',
    justifyContent: 'inherit'
  },
  buttonLeaf: {
    color: colors.blueGrey[800],
    padding: '10px 8px',
    justifyContent: 'flex-start',
    textTransform: 'none',
    letterSpacing: 0,
    width: '100%',
    fontWeight: theme.typography.fontWeightRegular,
    '&.depth-0': {
      fontWeight: theme.typography.fontWeightMedium
    }
  },
  icon: {
    color: theme.palette.icon,
    display: 'flex',
    alignItems: 'center',
    marginRight: theme.spacing(1)
  },
  copyIcon: {
    display: 'flex',
    alignItems: 'center',
  },
  iconColor: {
    color: theme.palette.icon,
  },
  expandIcon: {
    marginLeft: 'auto',
    height: 16,
    width: 16
  },
  totalItems: {
    marginLeft: theme.spacing(1)
  },
  label: {
    display: 'flex',
    alignItems: 'center',
    marginLeft: 'auto'
  },
  mla: {
    marginLeft: 'auto',
    opacity: 0,
    marginTop: -3,
    marginBottom: -3,
    transition: 'opacity 150ms'
  },
  active: {
    color: theme.palette.primary.main,
    backgroundColor: theme.palette.background.default,
    fontWeight: theme.typography.fontWeightMedium,
    '& $icon': {
      color: theme.palette.primary.main
    },
    '& $copyIcon': {
      color: theme.palette.primary.main
    }
  }
}));

let clipboard = null;

const NavigationListItem = props => {
  const {
    count,
    page,
    handleDuplicate,
    handleOverDrop,
    handleSelect,
    handleShare,
    depth,
    children,
    className,
    deleteFolder,
    onOpenModal,
    open: openProp,
    label: Label,
    shared,
    ...rest
  } = props;

  const { id, name, folderID } = page;

  const classes = useStyles();
  const duplicateRef = useRef(null);
  const [open, setOpen] = useState(openProp);
  const [openDelete, setOpenDelete] = useState(false);
  const [openPopover, setOpenPopover] = useState(false);

  const handleToggle = () => {
    setOpen(open => !open);
  };

  const handleCopyKeyDown = (folder, id) => event => {
    if (event.ctrlKey && event.keyCode === 88) {
      clipboard = { folder, id };
    }
  };

  const handlePasteKeyDown = folder => event => {
    if (event.ctrlKey && event.keyCode === 86 && clipboard) {
      event.dataTransfer = { getData: format => clipboard[format] }
      event.clear = () => clipboard = null;
      handleOverDrop(event.shiftKey ? 0 : folder)(event);
    }
  };

  let paddingLeft = 8;

  if (depth > 0) {
    paddingLeft = (32 + 8) * depth;
  }

  const style = {
    paddingLeft
  };

  if (children) {
    return (
      <Fragment>
        <ListItem
          {...rest}
          className={clsx(classes.item, className)}
          disableGutters
          onDragOver={e => e.preventDefault()}
          onDrop={handleOverDrop(folderID)}
          onKeyDown={handlePasteKeyDown(folderID)}
        >
          <div className={classes.itemLeaf}>
            <Button
              className={classes.button}
              component="div"
              onClick={handleToggle}
              style={style}
            >
              {open ?
                <FolderOpenIcon className={classes.icon} /> :
                <FolderIcon className={classes.icon} />
              }
              {name}
              {count > 0 && (
                <Typography
                  className={classes.totalItems}
                  variant="body2"
                >
                  ({count > 99 ? '99+' : count})
                </Typography>
              )}
              <div className={classes.mla}>
                <Tooltip title={'Delete ' + name}>
                  <IconButton
                    onClick={e => {
                      e.preventDefault();
                      e.stopPropagation();
                      if (count) setOpenDelete(true);
                      else deleteFolder(folderID);
                    }}
                    onFocus={e => e.stopPropagation()}
                    size="small"
                  >
                    <DeleteForeverIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
                <Tooltip title={'Add repository to ' + name}>
                  <IconButton
                    onClick={e => {
                      e.preventDefault();
                      e.stopPropagation();
                      onOpenModal(folderID);
                    }}
                    onFocus={e => e.stopPropagation()}
                    size="small"
                  >
                    <AddIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </div>
            </Button>
          </div>
          <Collapse in={open}>{children}</Collapse>
        </ListItem>
        <DeleteModal
          content={`Your are deleting a folder with ${count} repository(s). All repositories, including their datasets, will be permanently deleted from your account and from accounts that the repositories are shared with.`}
          onClose={() => setOpenDelete(false)}
          onDelete={() => deleteFolder(folderID)}
          open={openDelete}
          title={`Delete ${name} Folder`}
        />
      </Fragment>
    );
  } else {
    return (
      <Fragment>
        <ListItem
          {...rest}
          className={clsx(classes.itemLeaf, className)}
          disableGutters
        >
          <Button
            activeClassName={classes.active}
            className={clsx(classes.buttonLeaf, `depth-${depth}`)}
            component={CustomRouterLink}
            disableRipple
            exact
            onClick={() => handleSelect(page)}
            onDragStart={e => {
              e.dataTransfer.setData('folder', folderID);
              e.dataTransfer.setData('id', id);
              e.dataTransfer.setDragImage(document.getElementById(`i-${id}`), 50, 50);
            }}
            onKeyDown={handleCopyKeyDown(folderID, id)}
            style={style}
            to={`/repositories/${id}`}
          >
            <span
              className={classes.buttonLabel}
              draggable
              id={`i-${id}`}
            >
              <InsertDriveFileIcon className={classes.icon} />
              {name}
              {Label && (
                <span className={classes.label}>
                  <Label />
                </span>
              )}
              <div className={classes.mla}>
                <Tooltip title={'Share ' + name}>
                  <IconButton
                    onClick={e => {
                      e.preventDefault();
                      e.stopPropagation();
                      handleShare();
                    }}
                    onFocus={e => e.stopPropagation()}
                    size='small'
                  >
                    <ShareIcon
                      className={clsx(classes.copyIcon, !shared || !shared[page.id] || !shared[page.id].recepients.length ? classes.iconColor:'')}
                      color={shared && shared[page.id] && shared[page.id].recepients.length && 'primary' || undefined}
                      fontSize='small'
                    />
                  </IconButton>
                </Tooltip>
                <Tooltip
                  title={'Duplicate ' + name}
                >
                  <IconButton
                    onClick={e => {
                      e.preventDefault();
                      e.stopPropagation();
                      setOpenPopover(true);
                    }}
                    onFocus={e => e.stopPropagation()}
                    ref={duplicateRef}
                    size="small"
                  >
                    <FileCopyIcon
                      className={classes.copyIcon}
                      fontSize="small"
                    />
                  </IconButton>
                </Tooltip>
              </div>
            </span>
          </Button>
        </ListItem>
        <Popover
          anchorEl={duplicateRef.current}
          onClose={() => setOpenPopover(false)}
          onSubmit={handleDuplicate}
          open={openPopover}
          title={'Duplicate ' + name}
        />
      </Fragment>
    );
  }
};

NavigationListItem.propTypes = {
  children: PropTypes.node,
  className: PropTypes.string,
  count: PropTypes.number,
  deleteFolder: PropTypes.func,
  depth: PropTypes.number.isRequired,
  handleDuplicate: PropTypes.func,
  handleOverDrop: PropTypes.func,
  handleSelect: PropTypes.func,
  handleShare: PropTypes.func,
  label: PropTypes.node,
  onOpenModal: PropTypes.func,
  open: PropTypes.bool,
  page: PropTypes.object.isRequired,
  shared: PropTypes.object
};

NavigationListItem.defaultProps = {
  depth: 0,
  open: false,
  page: {},
};

export default NavigationListItem;
